<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div>
  <div class="image-selector-wrapper" :class="{docked}" v-show="imageSelectorEnabled">
    <!-- Docked: same header family as the toolbar flyout cards (Pipelines, Properties...). -->
    <flyout-header v-if="docked" class="dock-hd" :title="$t('images')" @close="imageSelectorEnabled = false" />
    <b-loading :is-full-page="false" :active="loading" />
      <div class="header">
        <div class="is-toolbar">
          <b-input class="search-images" :value="searchString" @input="debounceSearchString"
                   :placeholder="$t('search-placeholder')"
                   type="search" icon="search"
          />

          <!-- Filters / Tags / Image-groups: matching buttons that each open their
               selector in a fixed popover (opens upward, never clipped by the drawer).
               One shared click-outside so switching between them works cleanly. -->
          <div class="is-filtergroup" v-click-outside="closePopover">
            <div class="is-filterwrap">
              <button ref="filtersBtn" class="is-filters-btn" :class="{open: openPopover === 'filters'}" @click="togglePopover('filters')">
                <i class="fas fa-sliders-h"></i>
                {{ $t('filters') }}
                <span v-if="activeFilterCount" class="is-badge">{{ activeFilterCount }}</span>
              </button>
              <div v-if="openPopover === 'filters' && !docked" class="is-fb-pop" :style="popStyle">
                <image-filter-builder :value="filters" :groups="filterableGroups" @input="filters = $event" />
              </div>
            </div>

            <div class="is-filterwrap">
              <button ref="tagsBtn" class="is-filters-btn" :class="{open: openPopover === 'tags'}" @click="togglePopover('tags')">
                <i class="fas fa-tag"></i>
                {{ $t('tags') }}
                <span v-if="tagsCount" class="is-badge">{{ tagsCount }}</span>
              </button>
              <div v-if="openPopover === 'tags' && !docked" class="is-fb-pop is-pop-sm" :style="popStyle">
                <ims-multiselect v-model="selectedTags" :options="availableTags"
                  label="name" track-by="id" :multiple="true" :allPlaceholder="$t('all')" />
              </div>
            </div>

            <div class="is-filterwrap">
              <button ref="groupsBtn" class="is-filters-btn" :class="{open: openPopover === 'groups'}" @click="togglePopover('groups')">
                <i class="far fa-images"></i>
                {{ $t('image-groups') }}
                <span v-if="groupsCount" class="is-badge">{{ groupsCount }}</span>
              </button>
              <div v-if="openPopover === 'groups' && !docked" class="is-fb-pop is-pop-sm" :style="popStyle">
                <ims-multiselect
                    v-model="selectedImageGroups"
                    :options="availableImageGroups"
                    :label="'name'"
                    track-by="id"
                    multiple
                    :allPlaceholder="$t('all-image-groups')"
                />
              </div>
            </div>
          </div>
        </div>

        <button v-if="!docked" class="delete" @click="imageSelectorEnabled = false"></button>
      </div>

      <!-- Docked: the filter/tags/groups editors open INLINE between the toolbar and the
           slide cards - the dock's whole point is push-not-cover, and the floating popover
           was hovering over the very slides being filtered. -->
      <div v-if="docked && openPopover" class="inline-pop">
        <image-filter-builder v-if="openPopover === 'filters'"
          :value="filters" :groups="filterableGroups" @input="filters = $event" />
        <ims-multiselect v-else-if="openPopover === 'tags'"
          v-model="selectedTags" :options="availableTags"
          label="name" track-by="id" :multiple="true" :allPlaceholder="$t('all')" />
        <ims-multiselect v-else-if="openPopover === 'groups'"
          v-model="selectedImageGroups" :options="availableImageGroups"
          :label="'name'" track-by="id" multiple :allPlaceholder="$t('all-image-groups')" />
      </div>
    <template v-if="!loading">
      <div class="content-wrapper" v-if="error">
        <b-message type="is-danger" has-icon icon-size="is-small">
          <h2> {{ $t('error') }} </h2>
          <p> {{ $t('unexpected-error-info-message') }} </p>
        </b-message>
      </div>
      <div v-else class="image-selector">
        <div class="card" v-for="image in images" :key="image.id" :class="{active: alreadyAdded(image)}">
          <div class="card-media">
            <a
              class="card-image"
              @click="addImage(image)"
              :style="'background-image: url(' + appendShortTermToken(imageThumbUrl(image), shortTermToken) + ')'"
            ></a>
            <!-- Slide label beside the tissue thumbnail, when the slide carries one
                 (same source as the viewer's "Slide label" card); hidden per-image on 404. -->
            <a v-if="image.macroURL && !labelFailed[image.id]" class="card-slide-label" @click="addImage(image)">
              <image-thumbnail
                :image="image"
                :label="true"
                :size="200"
                :key="`${image.id}-tray-label`"
                :extra-parameters="{authorization: 'Bearer ' + shortTermToken}"
                @error="$set(labelFailed, image.id, true)"
              />
            </a>
          </div>
          <div class="card-content">
            <div class="content">
              <a @click="addImage(image)">
                <image-name :image="image" />
              </a>
            </div>
          </div>
        </div>

        <button class="button" v-if="!docked && nbImagesDisplayed < nbFilteredImages" @click="more()">
          {{$t('button-more')}}
        </button>

        <div class="has-text-grey no-result" v-if="nbFilteredImages === 0">
          <em>{{$t('no-result')}}</em>
        </div>

        <div class="space">&nbsp;</div>
      </div>
    </template>

    <!-- Docked: real pagination as a fixed footer (the list above scrolls; this stays put).
         Projects will grow - a More button that only appends does not. -->
    <div v-if="docked && nbFilteredImages > trayPerPage" class="tray-pager">
      <pager :total="nbFilteredImages" :per-page="trayPerPage" :current.sync="trayPage" />
    </div>
  </div>

  <a
    class="image-selector-button"
    v-show="!imageSelectorEnabled && !docked"
    @click="imageSelectorEnabled = true"
    v-tooltip="{content: $t('add-image'), placement: 'top'}"
  >
    <i class="far fa-images isb-icon"></i>
    <span class="isb-label">{{ $t('images') }}</span>
    <span class="isb-count" v-if="nbFilteredImages">{{ nbFilteredImages }}</span>
    <i class="fas fa-caret-up isb-caret"></i>
  </a>
</div>
</template>

<script>
import {get,syncMultiselectFilter} from '@/utils/store-helpers';
import constants from '@/utils/constants.js';
import FlyoutHeader from './FlyoutHeader';
import {IMAGE_FORMAT} from '@/utils/image-utils';

import ImageName from '@/components/image/ImageName';
import ImageThumbnail from '@/components/image/ImageThumbnail';
import Pager from '@/components/utils/Pager';
import ImsMultiselect from '@/components/form/ImsMultiselect';
import ImageFilterBuilder from '@/components/image/ImageFilterBuilder';
import {ImageGroupCollection, ImageInstanceCollection, TagCollection} from '@/api';
import {COLUMNS, COLUMN_GROUPS} from '@/utils/image-table-columns';
import {buildImageFilterExpression} from '@/utils/image-search';
import _ from 'lodash';
import {appendShortTermToken} from '@/utils/token-utils.js';

const storeOptions = {rootModuleProp: 'storeModule'};
const localSyncMultiselectFilter = (filterName, options) => syncMultiselectFilter(null, filterName, options, storeOptions);

export default {
  name: 'image-selector',
  components: {
    ImageThumbnail,
    Pager,
    FlyoutHeader,
    ImageName,
    ImsMultiselect,
    ImageFilterBuilder
  },
  data() {
    return {
      images: [],
      availableTags:[],
      imageGroups: [],
      searchString: '',
      selectedImageGroups: [],
      nbImagesDisplayed: 20,
      trayPage: 1,
      trayPerPage: 10,
      labelFailed: {},
      nbFilteredImages: 0,
      loading: true,
      error: false,

      // case-info-aware search + Filters builder (shared with the images page)
      filters: [],
      openPopover: null, // 'filters' | 'tags' | 'groups' | null
      popStyle: {},
      fetchToken: 0
    };
  },
  computed: {
    project: get('currentProject/project'),
    shortTermToken: get('currentUser/shortTermToken'),
    selectedTags: localSyncMultiselectFilter('selectedTags', 'availableTags'),
    storeModule() {
      return this.$store.getters['currentProject/currentProjectModule'] + 'listImages';
    },
    viewerModule() {
      return this.$store.getters['currentProject/currentViewerModule'];
    },
    docked() {
      // Dock layout (2026-09-03): render as a left column that pushes the maps right,
      // instead of the absolute bottom drawer. VIEWER_DOCK_LAYOUT=false restores the drawer.
      return constants.VIEWER_DOCK_LAYOUT !== false;
    },
    imageSelectorEnabled: {
      get() {
        return this.$store.getters['currentProject/currentViewer'].imageSelector;
      },
      set(value) {
        this.$store.commit(this.viewerModule + 'setImageSelector', value);
      }
    },
    viewerImagesIds() {
      return Object.values(this.$store.getters['currentProject/currentViewer'].images).map(image => image.imageInstance.id);
    },
    availableImageGroups() {
      return [{id: 'null', name: this.$t('no-image-group')}, ...this.imageGroups];
    },
    filterableGroups() {
      return COLUMN_GROUPS
        .map(g => ({...g, cols: COLUMNS.filter(c => c.group === g.id && c.filterable)}))
        .filter(g => g.cols.length);
    },
    activeFilterCount() {
      return this.filters.filter(f => f.operator === 'is-empty' || (f.value !== '' && f.value != null)).length;
    },
    tagsCount() {
      return this.selectedTags.length;
    },
    groupsCount() {
      const n = this.selectedImageGroups.length;
      return (n > 0 && n < this.availableImageGroups.length) ? n : 0;
    }
  },
  watch: {
    imageSelectorEnabled(open) {
      // One dock at a time: opening the Images tray closes any docked annotation list
      // (whatever path opened it - menu, shortcut, or code).
      if (open && this.docked) {
        const viewer = this.$store.getters['currentProject/currentViewer'];
        Object.keys(viewer.images).forEach((idx) => {
          const mod = this.$store.getters['currentProject/imageModule'](idx);
          if (viewer.images[idx].annotationsList && viewer.images[idx].annotationsList.open) {
            this.$store.commit(mod + 'setShowAnnotationsList', false);
          }
        });
      }
      // Opening/closing the docked panel resizes the map viewport - let OpenLayers recompute.
      this.$nextTick(() => this.$eventBus.$emit('updateMapSize'));
    },
    trayPage() {
      this.fetchImages();
    },
    searchString() {
      this.resetTrayAndFetch();
    },
    filters: {
      deep: true,
      handler() {
        this.resetTrayAndFetch();
      }
    },
    selectedImageGroups() {
      this.resetTrayAndFetch();
    },
    nbImagesDisplayed() {
      this.fetchImages();
    },
    async selectedTags() {
      if (!this.selectedTags.length) {
        this.images = [];
      } else {
        await this.fetchImages();
      }
    }
  },
  methods: {
    appendShortTermToken,
    debounceSearchString: _.debounce(async function (value) {
      this.searchString = value;
    }, 500),
    async addImage(image) {
      try {
        await image.fetch(); // refetch image to ensure we have latest version
        let slice = await image.fetchReferenceSlice();
        await this.$store.dispatch(this.viewerModule + 'addImage', {image, slices: [slice]});
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-add-viewer-image')});
      }
    },
    async fetchImages(loading = true) {
      if (loading) {
        this.loading = true;
      }
      this.fetchToken++;
      const token = this.fetchToken;

      try {
        let collection = new ImageInstanceCollection({
          filterKey: 'project',
          filterValue: this.project.id,
          max: this.docked ? this.trayPerPage : this.nbImagesDisplayed
        });

        // case-info-aware search + Filters builder → server-side filterExpression.
        // A single character is ignored (min length 2) to avoid a wide multi-field scan.
        const search = this.searchString.trim().length === 1 ? '' : this.searchString;
        const expr = buildImageFilterExpression({search, builderFilters: this.filters});
        if (expr.length) {
          collection.filterExpression = JSON.stringify(expr);
        }

        if (this.selectedTags.length > 0) {
          collection['tag'] = {
            in: this.selectedTags.map(option => option.id).join()
          };
        }

        if (this.selectedImageGroups.length > 0 && this.selectedImageGroups.length !== this.availableImageGroups.length) {
          collection['imageGroup'] = {
            in: this.selectedImageGroups.map(option => option.id).join()
          };
        }

        if (this.selectedImageGroups.length === 0) {
          this.images = [];
          this.nbFilteredImages = 0;
        } else {
          let data = (await collection.fetchPage(this.docked ? this.trayPage - 1 : 0));
          if (token !== this.fetchToken) {
            return; // a newer request superseded this one
          }
          this.images = data.array;
          this.nbFilteredImages = data.totalNbItems;
        }
      } catch (error) {
        if (token === this.fetchToken) {
          console.log(error);
          this.error = true;
        }
      }
      if (loading && token === this.fetchToken) {
        this.loading = false;
      }
    },

    togglePopover(key) {
      this.openPopover = (this.openPopover === key) ? null : key;
      if (this.openPopover && !this.docked) {
        this.$nextTick(() => this.positionPopover());
      }
    },
    closePopover() {
      // Docked: the editor is an inline section, not a popover - clicking into it (it lives
      // outside the button group) must not dismiss it. Close by re-clicking the button or Esc.
      if (this.docked) {
        return;
      }
      this.openPopover = null;
    },
    positionPopover() {
      const refName = {filters: 'filtersBtn', tags: 'tagsBtn', groups: 'groupsBtn'}[this.openPopover];
      const btn = refName && this.$refs[refName];
      if (!btn) {
        return;
      }
      const r = btn.getBoundingClientRect();
      // open upward: the popover's bottom sits just above the button
      this.popStyle = {
        left: Math.max(8, r.left) + 'px',
        bottom: (window.innerHeight - r.top + 8) + 'px'
      };
    },
    onWindowChange() {
      if (this.openPopover) {
        this.positionPopover();
      }
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.openPopover) {
        this.openPopover = null;
      }
    },
    async fetchImageGroups() {
      this.imageGroups = (await ImageGroupCollection.fetchAll({
        filterKey: 'project',
        filterValue: this.project.id
      })).array.filter(group => group.numberOfImages > 0);
    },
    async fetchTags() {
      this.availableTags = [{id: 'null', name: this.$t('no-tag')}, ...(await TagCollection.fetchAll()).array];
    },

    resetTrayAndFetch() {
      // A changed filter invalidates the page position; setting trayPage triggers its own
      // watcher, so only fetch directly when it is already 1 (fetchToken dedups any overlap).
      if (this.docked && this.trayPage !== 1) {
        this.trayPage = 1;
      } else {
        this.fetchImages();
      }
    },
    more() {
      this.nbImagesDisplayed += 20;
    },

    toggle() {
      this.imageSelectorEnabled = !this.imageSelectorEnabled;
    },

    shortkeyHandler(key) {
      if (key === 'toggle-add-image') {
        this.toggle();
      }
    },

    alreadyAdded(image) {
      return this.viewerImagesIds.includes(image.id);
    },
    imageThumbUrl(image) {
      return image.thumbURL(256, IMAGE_FORMAT);
    }
  },
  async created() {
    this.loading = true;
    await Promise.all([
      this.fetchImageGroups(),
      this.fetchImages(false),
      this.fetchTags()
    ]);
    this.selectedImageGroups = this.availableImageGroups.slice();
    this.loading = false;
  },
  mounted() {
    this.$eventBus.$on('shortkeyEvent', this.shortkeyHandler);
    window.addEventListener('resize', this.onWindowChange);
    window.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    this.$eventBus.$off('shortkeyEvent', this.shortkeyHandler);
    window.removeEventListener('resize', this.onWindowChange);
    window.removeEventListener('keydown', this.onKeydown);
  }
};
</script>

<style scoped>
.image-selector-wrapper {
  position: absolute;
  left: 0;
  bottom: 0;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
  display: flex;
  flex-direction: column;
  background: #fff;
  width: 100%;
  height: 20em;
  z-index: 150;
}

/* Dock layout: a left column in the viewer-body flex row - pushes the slide, never covers it. */
.image-selector-wrapper.docked ::v-deep .flyout-hd {
  margin: 0;
}

.image-selector-wrapper.docked {
  position: static;
  /* Responsive: browser zoom = fewer CSS pixels = a smaller screen. A fixed 420px ate
     39% of the viewport at 150% zoom and HALF an iPad portrait. 26.25vw keeps the exact
     desktop look at 1600px and shrinks proportionally below it. */
  width: clamp(260px, 26.25vw, 420px);
  min-width: clamp(260px, 26.25vw, 420px);
  height: 100%;
  box-shadow: none;
  border-right: 1px solid #e5e8ee;
  z-index: auto;
}

/* Vertical slide tray - the way WSI viewers present a case's slides: one thumbnail card
   per row, reading top to bottom, scrolling down the column. */
.image-selector-wrapper.docked .image-selector {
  flex-direction: column;
  align-items: stretch;
  overflow-y: auto;
  overflow-x: hidden;
}

.image-selector-wrapper.docked .card {
  min-width: 0;
  width: auto;
  flex: 0 0 auto;
  margin: 0.6em 0.75em 0.15em;
}

.image-selector-wrapper.docked .card-image {
  height: 8.5em;
}

.card-media {
  display: flex;
  gap: 4px;
}

.card-media .card-image {
  flex: 1 1 auto;
  min-width: 0;
}

.card-slide-label {
  flex: 0 0 34%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f6f9;
  border: 1px solid #e5e8ee;
  border-radius: 4px;
  overflow: hidden;
}

.card-slide-label ::v-deep img {
  max-width: 100%;
  max-height: 8.5em;
  object-fit: contain;
}

.tray-pager {
  flex: 0 0 auto;
  padding: 6px 8px;
  border-top: 1px solid #e5e8ee;
  background: #fff;
  display: flex;
  justify-content: center;
}

.image-selector-wrapper.docked .is-toolbar {
  flex-wrap: wrap;
  row-gap: 6px;
}

/* Docked inline editor: a flow section between the toolbar and the slide cards - it pushes
   the list down instead of hovering over it. Builder rows wrap at column width. */
.inline-pop {
  flex: 0 0 auto;
  max-height: 45%;
  overflow-y: auto;
  padding: 10px 12px;
  background: #fafbfc;
  border-bottom: 1px solid #e5e8ee;
}

/* The multiselect dropdown is absolutely positioned by default - inside this scrolling
   container it was CLIPPED and appeared to render over/below the slide cards. Static keeps
   it in flow: the editor grows, the cards get pushed, everything stays visible. */
.inline-pop ::v-deep .multiselect__content-wrapper {
  position: static;
  box-shadow: none;
}

.inline-pop ::v-deep .fb-row {
  flex-wrap: wrap;
  row-gap: 6px;
}

.inline-pop ::v-deep .fb-field {
  min-width: 0;
  flex: 1 1 160px;
}

.image-selector-wrapper.docked .tray-count {
  text-align: center;
  font-size: 11px;
  color: #5a6172;
  padding: 0.35em 0 0.1em;
}

.image-selector-wrapper.docked .image-selector > .button {
  margin: 0.35em 0.75em 0.9em;
}

.header {
  padding: 0.6em 0.75em 0.4em;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.is-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
}

.header .filter-label {
  margin: 0 2px 0 6px;
}

.search-images {
  margin-right: 0;
  min-width: 16em;
}

/* Filters / Tags / Image-groups buttons + fixed (upward) popovers, mirroring the images page */
.is-filtergroup { display: inline-flex; align-items: center; gap: 10px; }
.is-filterwrap { position: relative; display: inline-flex; }
.is-filters-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 13px;
  border-radius: 10px;
  border: 1px solid #e2e6ec;
  background: #fff;
  color: #3a4452;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
}
.is-filters-btn:hover { background: #f4f6fa; }
.is-filters-btn.open { border-color: #1e63d0; background: #f4f7fe; color: #1e63d0; }
.is-badge {
  background: #1e63d0;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}
.is-fb-pop {
  position: fixed;
  z-index: 200;
  min-width: 560px;
  max-width: calc(100vw - 32px);
  max-height: 52vh;
  overflow: auto;
  overscroll-behavior: contain;
  padding: 16px 18px 14px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e8eaef;
  box-shadow: 0 18px 48px rgba(31, 41, 55, 0.18);
}
/* tags / image-groups popover: narrower, and overflow visible so the multiselect's
   own dropdown list isn't clipped */
.is-fb-pop.is-pop-sm {
  min-width: 300px;
  max-height: none;
  overflow: visible;
  padding: 14px 16px;
}

.search {
  margin-top: 0.25em;
}

.filters {
  display: flex;
  padding: 0;
  background: None;
}

.filter-label {
  font-size: 0.9em;
  margin-top: 0.7rem;
  margin-left: 1em;
  margin-right: 0.5em;
}

.delete {
  margin-top: 1.2rem;
}

.image-selector {
  width: 100%;
  overflow: auto;
  display: flex;
  align-items: center;
  flex: 1;
}

.card {
  display: inline-block;
  min-width: 12em;
  flex: 0;
  box-sizing: border-box;
  margin: 0.75em;
}

.card-image {
  display: inline-block;
  width: 100%;
  height: 9.5em;
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
  background-color: white;
  border-bottom: 1px solid #ddd;
}

.card-content {
  padding: 0.75em;
  font-size: 0.8rem;
  overflow-wrap: break-word;
  overflow: hidden;
  height: 5em;
}

.space {
  margin-left: 0.5em;
}

/* UI-5 (5d): "Images" pull-tab — white rounded-top tab, centered at the bottom
   as a pair with the Annotations tab. Full-float viewer: no docked panel bar,
   so both anchor to the true map centre (50%). */
.image-selector-button {
  background: #fff;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(calc(-100% - 4px));
  height: auto;
  padding: 0.5rem 1.1rem;
  border: 1px solid #e5e8ee;
  border-bottom: 0;
  border-radius: 10px 10px 0 0;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: #5a6172;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  box-sizing: border-box;
  box-shadow: 0 -4px 14px rgba(15, 17, 22, 0.06);
  z-index: 100;
  cursor: pointer;
}

.image-selector-button:hover {
  background: #fafbfc;
  color: #0e1116;
}

.image-selector-button .isb-icon {
  color: #5a6172;
}

.image-selector-button:hover .isb-icon {
  color: #1e63d0;
}

.image-selector-button .isb-count {
  font-family: 'JetBrains Mono', monospace;
  background: #e4ecfb;
  color: #17509e;
  padding: 1px 7px;
  border-radius: 8px;
  font-size: 10px;
  letter-spacing: 0;
}

.image-selector-button .isb-caret {
  font-size: 0.7rem;
  opacity: 0.85;
}

.active {
  box-shadow: 0 2px 3px rgba(39, 120, 173, 0.75), 0 0 0 1px rgba(39, 120, 173, 0.75);
  font-weight: 600;
}

.no-result {
  margin: 2em;
}
</style>
